import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useGetClientOverviewQuery } from "../../store/apis/clientPortalApi";

// Empresa seleccionada en la vista de cliente. La comparten el sidebar (que la
// cambia) y el inicio (que solo enseña sus campañas). Se recuerda en el
// navegador; si no está o ya no es del usuario, vale la primera.

const STORAGE_KEY = "clientView.companyId";
const listeners = new Set<() => void>();

const readStored = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

let selectedId: string | null = readStored();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSelectedId = () => selectedId;

export const setClientCompanyId = (companyId: string) => {
  if (companyId === selectedId) return;
  selectedId = companyId;
  try {
    window.localStorage.setItem(STORAGE_KEY, companyId);
  } catch {
    // Sin almacenamiento la selección dura lo que la pestaña.
  }
  listeners.forEach((listener) => listener());
};

/** Las empresas del cliente, la seleccionada y solo las campañas de esa. */
export const useClientCompany = () => {
  const query = useGetClientOverviewQuery();
  const storedId = useSyncExternalStore(subscribe, getSelectedId);
  const data = query.data;

  const companies = useMemo(() => data?.companies ?? [], [data]);
  const company =
    companies.find((c) => c.id === storedId) ?? companies[0] ?? null;
  const companyId = company?.id ?? null;
  const campaigns = useMemo(
    () =>
      companyId
        ? (data?.campaigns ?? []).filter((c) => c.companyId === companyId)
        : [],
    [data, companyId]
  );

  const selectCompany = useCallback((id: string) => setClientCompanyId(id), []);

  return { ...query, companies, company, campaigns, selectCompany };
};
